import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getInsight } from "../lib/api";
import { useT } from "../lib/i18n";
import { openBooking } from "../lib/booking";

export default function InsightDetail() {
    const { slug } = useParams();
    const { t, lang } = useT();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        setError(null);
        getInsight(slug, lang)
            .then((d) => {
                setPost(d);
                document.title = `${d.title}, Casati Consulting`;
            })
            .catch(() => setError(lang === "it" ? "Articolo non trovato." : "Article not found."))
            .finally(() => setLoading(false));
    }, [slug, lang]);

    return (
        <div className="bg-[#F9F9F7] text-[#1C1C1C]">
            <section className="pt-16 md:pt-20 pb-8">
                <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-8">
                    <Link
                        to="/insights"
                        className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-bold text-[#1C1C1C]/60 hover:text-[#1C1C1C] link-underline"
                        data-testid="insight-back"
                    >
                        <ArrowLeft size={14} strokeWidth={1.5} />
                        {t.common.backToInsights}
                    </Link>
                </div>
            </section>

            {renderInsightDetailBody({ loading, error, post, t, lang, navigate })}
        </div>
    );
}

function renderInsightDetailBody({ loading, error, post, t, lang, navigate }) {
    if (loading) {
        return (
            <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 text-[#1C1C1C]/60">
                {t.common.loading}
            </div>
        );
    }
    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 md:px-12 py-20" data-testid="insight-error">
                <p className="text-[#1C1C1C]/70">{error}</p>
            </div>
        );
    }
    if (!post) return null;
    return (
        <>
            <article className="max-w-3xl mx-auto px-6 md:px-12 lg:px-8 pb-20" data-testid="insight-article">
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold mt-6" style={{ color: "#9B7A58" }}>
                    {post.category} · {post.read_minutes} {t.common.minRead}
                </p>
                <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.0]" style={{ letterSpacing: "-0.03em" }}>
                    {post.title}
                </h1>
                <p className="mt-6 text-base text-[#1C1C1C]/55">
                    {t.common.by} {post.author} ·{" "}
                    {new Date(post.published_at).toLocaleDateString(lang === "it" ? "it-CH" : "en-CH", { day: "numeric", month: "long", year: "numeric" })}
                </p>

                <div className="mt-12 space-y-6 text-lg leading-[1.75] text-[#1C1C1C]/85 font-light">
                    {post.body.split("\n\n").map((para, i) => (
                        <p key={`${post.slug}-p${i}`} className="whitespace-pre-line">{para}</p>
                    ))}
                </div>
            </article>

            <section className="border-t hair py-16 bg-[#F4F3EF]">
                <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <p className="font-display text-2xl tracking-tight font-medium max-w-md" style={{ letterSpacing: "-0.02em" }}>
                        {t.insightDetail.question}
                    </p>
                    <button
                        type="button"
                        onClick={() => openBooking(navigate)}
                        className="inline-flex items-center gap-2 px-5 h-11 bg-[#1C1C1C] text-[#F9F9F7] text-[12px] uppercase tracking-[0.22em] font-bold hover:bg-[#9B7A58] transition-colors"
                        data-testid="insight-cta-contact"
                    >
                        {t.cta.contact}
                        <ArrowUpRight size={14} strokeWidth={1.5} />
                    </button>
                </div>
            </section>
        </>
    );
}
